import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import Colors from 'pubweb-smokey/dist/colors';

import Form from '@components/Shared/Form';
import { runCommonValidation } from 'pubweb-smokey/dist/utils/validation';
import TextField from '@components/Shared/TextField';
import Button from '@components/Shared/Buttons/Button';
import CheckBox from 'pubweb-smokey/dist/components/CheckBox';
import RadioButtonGroup from 'pubweb-smokey/dist/components/RadioButton/RadioButtonGroup';
import LoadingIndicator from '@components/Shared/LoadingIndicator/LoadingIndicator';
import { stateItems } from '@utils/stateUtils';
import { submitCcpaCATForm } from '@services/formService';

import { CCPAFormStyles } from './CCPAForm.styled';

const SuggestionBox = dynamic(
  () =>
    import('@components/Shared/DropDownList/DropDownList').then(
      (mod) => mod.SuggestionBox
    ),
  {
    ssr: false,
  }
);

// Is the state California.
const isCA = (value) => {
  return value == 'ca';
};

// Is the state any one of the states allowed for the form.
const isValidState = (value) => {
  const validStates = ['co', 'va', 'ca', 'tx', 'or', 'de', 'ne'];

  return validStates.includes(value);
};

// The full name and abbreviation of each states privacy act.
const privacyActText = {
  ca: { name: 'California Consumer Privacy Act', abbrev: 'CCPA' },
  va: { name: 'Virginia Consumer Data Protection Act', abbrev: 'VCDPA' },
  co: { name: 'Colorado Privacy Act', abbrev: 'CPA' },
  tx: { name: 'Texas Data Privacy and Security Act', abbrev: 'TDPSA' },
  or: { name: 'Oregon Consumer Privacy Act', abbrev: 'OCPA' },
  de: { name: 'Delaware Personal Data Privacy Act', abbrev: 'DPDPA' },
  ne: { name: 'Nebraska Data Privacy Act', abbrev: 'NEDPA' },
};

const CCPAForm = () => {
  const methodItems = [
    { value: 'viaEmail', text: 'Email' },
    { value: 'viaAddress', text: 'Address on file' },
  ];

  const [formErrors, setFormErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [submitPressed, setSubmitPressed] = useState(false);
  const [disclosure, setDisclosure] = useState(false);
  const [correction, setCorrection] = useState(false);
  const [appeal, setAppeal] = useState(false);
  const [deletion, setDeletion] = useState(false);
  const [customer, setCustomer] = useState(false);
  const [businessContact, setBusinessContact] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(false);
  const [formerEmployee, setFormerEmployee] = useState(false);
  const [applicant, setApplicant] = useState(false);
  const [categories, setCategories] = useState(false);
  const [method, setMethod] = useState(null);
  const [acknowledge, setAcknowledge] = useState(false);
  const [agent, setAgent] = useState(false);
  const [stateSelection, setStateSelection] = useState();
  const [knowAccess, setKnowAccess] = useState();

  const [filteredStateItems] = useState(
    stateItems?.filter((item) => isValidState(item?.value))
  );

  const [displayRequestOptions, setDisplayRequestOptions] = useState({
    categories: true,
    correction: true,
    disclosure: true,
    deletion: true,
    knowAccess: false,
    appeal: false,
  });

  const onCategoriesChange = (checked) => {
    setCategories(checked);
  };

  const onKnowAccessChange = (checked) => {
    setKnowAccess(checked);
  };

  const onDisclosureChange = (checked) => {
    setDisclosure(checked);
  };

  const updateMethod = (option) => {
    setMethod(option);
  };

  const submitClick = () => {
    setSubmitPressed(true);
  };

  const onCorrectionChange = (checked) => {
    setCorrection(checked);
  };

  const onAppealChange = (checked) => {
    setAppeal(checked);
  };

  const onStateSelect = (item) => {
    setStateSelection(item);
    if (formErrors && formErrors.state) {
      const errors = { ...formErrors };
      errors.state = null;
      setFormErrors(errors);
    }

    // Set the request options to display.
    if (isCA(item?.value)) {
      // States: CA
      setDisplayRequestOptions({
        categories: true,
        correction: true,
        disclosure: true,
        deletion: true,
        knowAccess: false,
        appeal: false,
      });
    } else {
      // States: VA, CO, TX, OR
      setDisplayRequestOptions({
        categories: false,
        correction: true,
        disclosure: false,
        deletion: true,
        knowAccess: true,
        appeal: true,
      });
    }

    // Reset both acknowledge options (agent or acknowledge) when changing the state selection. Some states have two options.
    // Prevents setting an option and then switching to a state that does not have the option.
    setAcknowledge(false);
    setAgent(false);
    setMethod(null);
  };

  const onEmployeeTypeChecked = (checked, type) => {
    if (type == 'current' && checked) {
      setCurrentEmployee(checked);
      setFormerEmployee(!checked);
    } else if (type == 'former' && checked) {
      setCurrentEmployee(!checked);
      setFormerEmployee(checked);
    } else if (type == 'former' && !checked) {
      setFormerEmployee(checked);
    } else if (type == 'current' && !checked) {
      setCurrentEmployee(checked);
    }
  };

  // Make the 'acknowledge' or 'agent' selection mutually exclusive
  const onAcknowledgeOrAgentChecked = (checked, type) => {
    if (type == 'acknowledge' && checked) {
      setAcknowledge(checked);
      setAgent(!checked);
    } else if (type == 'agent' && checked) {
      setAcknowledge(!checked);
      setAgent(checked);
    } else if (type == 'acknowledge' && !checked) {
      setAcknowledge(checked);
    } else if (type == 'agent' && !checked) {
      setAgent(checked);
    }
  };

  const validateForm = (formObj) => {
    var formErrors = runCommonValidation(formObj);

    const specialErrorMessages = {
      request: 'Please provide a request type before submitting.',
      method: 'Please provide your preferred method to receive the disclosure.',
      disclosureDetails: 'Please choose at least one option above.',
      acknowledge: 'Please confirm the acknowledgement before continuing.',
      address: 'Please provide a street address.',
      city: 'Please provide a city.',
      state: 'Please choose a state from the list.',
    };
    let otherErrors = {};

    // Request type selected?
    if (
      !formObj?.categories?.value &&
      !formObj?.disclosure?.value &&
      !formObj?.deletion?.value &&
      !formObj?.correction?.value &&
      !formObj?.appeal?.value &&
      !formObj?.knowAccess?.value &&
      submitPressed
    ) {
      otherErrors.request = specialErrorMessages.request;
      otherErrors.hasErrors = true;
    }

    // Is a response method required?
    if (
      (formObj?.categories?.value ||
        formObj?.disclosure?.value ||
        formObj?.knowAccess?.value) &&
      !method &&
      submitPressed
    ) {
      otherErrors.method = specialErrorMessages.method;
      otherErrors.hasErrors = true;
    }
    // capture Employment details
    if (
      formObj?.disclosure?.value &&
      isCA(formObj?.state?.value) &&
      !(customer || businessContact || applicant) &&
      !formerEmployee &&
      !currentEmployee &&
      submitPressed
    ) {
      otherErrors.disclosureDetails = specialErrorMessages.disclosureDetails;
      otherErrors.hasErrors = true;
    }

    // Address
    if (formObj.address && formObj.address.touched) {
      if (formObj.address.value.trim().length < 2) {
        otherErrors.address = specialErrorMessages.address;
      }
    }

    // City
    if (formObj.city && formObj.city.touched) {
      if (formObj.city.value.trim().length < 2) {
        otherErrors.city = specialErrorMessages.city;
      }
    }

    // State
    // The SuggestionBox seems to add two fields on the form 'state' and 'state-text'.
    // I'm using 'state-text' to look up a 2 char state abbreviation.
    if (formObj['state-text'] && formObj['state-text'].touched) {
      const stateVal = formObj['state-text'].value
        ? formObj['state-text'].value.trim().toLowerCase()
        : '';
      let stateItm = undefined;
      if (stateVal.length < 2) {
        otherErrors.state = specialErrorMessages.state;
      } else if (stateVal.length > 2) {
        // User may have entered a full state name (look for 'text')
        stateItm = stateItems.find(
          (s) => s.text.toLowerCase() === stateVal.toLowerCase()
        );
      } else if (stateVal.length === 2) {
        // User may have entered a state abbreviation (look for 'value')
        stateItm = stateItems.find(
          (s) => s.value.toLowerCase() === stateVal.toLowerCase()
        );
      }

      // Only allow specific states. Note: stateItm has value (abbreviation) and text (full name) fields
      if (isValidState(stateItm?.value)) {
        setStateSelection(stateItm);
      } else {
        setStateSelection();
        otherErrors.state = specialErrorMessages.state;
      }
    }

    // Is an acknowledgement required?
    if (
      (formObj?.deletion?.value || formObj?.disclosure?.value) &&
      !(acknowledge || agent) &&
      submitPressed
    ) {
      otherErrors.acknowledge = specialErrorMessages.acknowledge;
      otherErrors.hasErrors = true;
    }

    otherErrors.hasErrors =
      Object.keys(otherErrors).filter((k) => !!otherErrors[k]).length > 0;

    const allErrors = { ...formErrors, ...otherErrors };
    allErrors.hasErrors = formErrors.hasErrors || otherErrors.hasErrors;

    setSubmitPressed(false);
    setFormErrors(allErrors);
    return allErrors.hasErrors;
  };

  const formObj = {};

  const requestInfo = (formObj) => {
    if (formSubmitting || formSubmitted) {
      return;
    }

    setFormSubmitting(true);

    let userId = null; //this.props.user ? this.props.user.id : null;
    let preferredContactMethod = null;
    if (formObj?.disclosure || formObj?.categories || formObj?.knowAccess) {
      preferredContactMethod =
        method?.value === 'viaEmail'
          ? 'email'
          : method?.value === 'viaAddress'
            ? 'address'
            : null;
    }

    // Note: If a request for deletion or disclosure is made, the user must acknowledge California residency via a checkbox.
    //       The acknowledgement is not sent as part of the form because the form itself is acknowlegement for those cases.
    const formData = {
      firstName: formObj.firstName ? formObj.firstName.trim() : undefined,
      lastName: formObj.lastName ? formObj.lastName.trim() : undefined,
      phone: formObj.phone ? formObj.phone.trim() : undefined,
      email: formObj.email ? formObj.email.trim() : undefined,
      knowAccess: formObj.knowAccess,
      categories: formObj.categories,
      disclosure: formObj.disclosure,
      deletion: formObj.deletion,
      correction: correction,
      appeal: formObj.appeal ? appeal : undefined,
      preferredContactMethod: preferredContactMethod,
      customer: customer,
      businessContact: businessContact,
      currentEmployee: currentEmployee,
      formerEmployee: formerEmployee,
      applicant: applicant,
      authorizedAgent: agent,
      source: formObj.source,
      userId: userId,
      address: formObj.address ? formObj.address.trim() : undefined,
      city: formObj.city ? formObj.city.trim() : undefined,
      state:
        stateSelection && stateSelection.value
          ? stateSelection.value.trim()
          : undefined,

      zip: formObj.zip,
    };

    submitCcpaCATForm(formData)
      .then(() => {
        setFormSubmitted(true);
        setFormSubmitting(false);
      })
      .catch(() => {
        setFormSubmitted(false);
        setFormSubmitting(false);
      });
  };

  useEffect(() => {
    // Reset the selected delivery method if both check boxes are cleared
    if (!disclosure && !categories) {
      setMethod(null);
    }
    // Reset the acknowledgement (and agent) if both disclosure and deletion check boxes are cleared
    if (!disclosure && !deletion) {
      setAcknowledge(false);
      setAgent(false); // California has two acknowledge options (agent or acknowledge)
    }
  }, [disclosure, categories, deletion]);

  return (
    <CCPAFormStyles>
      <div className="ccpa-form-container">
        <h3 className="title-text">Data Privacy Request Form</h3>

        {!formSubmitted && (
          <div>
            <p className="heading-text caption short">
              To exercise your rights under the states listed below, as
              described in our
              <a href="https://www.claytonhomes.com/privacy/">
                {' '}
                privacy policies
              </a>
              , please submit a verifiable consumer request by submitting the
              requested information. Please note that we limit these requests
              per year. You may only submit one request at a time, and you will
              need to repeat the process for each new request.
            </p>

            <p className="heading-text caption short align-left">
              States:
              <br></br>
              {privacyActText.ca.name} ({privacyActText.ca.abbrev})<br></br>
              {privacyActText.va.name} ({privacyActText.va.abbrev})<br></br>
              {privacyActText.co.name} ({privacyActText.co.abbrev})<br></br>
              {privacyActText.tx.name} ({privacyActText.tx.abbrev})<br></br>
              {privacyActText.or.name} ({privacyActText.or.abbrev})<br></br>
              {privacyActText.de.name} ({privacyActText.de.abbrev})<br></br>
              {privacyActText.ne.name} ({privacyActText.ne.abbrev})
            </p>
            <Form onFormValidate={validateForm} onSubmit={requestInfo}>
              <input
                type="hidden"
                name="source"
                id="source"
                defaultValue="data-privacy-request-form"
              />
              <div className="form-row shared-form-row">
                <TextField
                  id="firstName"
                  defaultValue={formObj.firstName}
                  errorMessage={formErrors.firstName}
                  textFieldStyle="primary"
                  label="First Name"
                  automationId="ccpa-form-first-name-textfield"
                />
                <TextField
                  id="lastName"
                  defaultValue={formObj.lastName}
                  errorMessage={formErrors.lastName}
                  textFieldStyle="primary"
                  label="Last Name"
                  automationId="ccpa-form-last-name-textfield"
                />
              </div>
              <div className="form-row shared-form-row">
                <TextField
                  id="phone"
                  defaultValue={formObj.phone}
                  errorMessage={formErrors.phone}
                  textFieldStyle="primary"
                  label="Phone Number"
                  automationId="ccpa-form-phone-textfield"
                />
                <TextField
                  id="email"
                  defaultValue={formObj.email}
                  errorMessage={formErrors.email}
                  textFieldStyle="primary"
                  label="Email"
                  automationId="ccpa-form-email-textfield"
                />
              </div>
              <div className="form-row">
                <TextField
                  id="address"
                  defaultValue={formObj.address}
                  errorMessage={formErrors.address}
                  textFieldStyle="primary"
                  label="Address"
                  automationId="ccpa-form-address-textfield"
                />
              </div>
              <div className="form-row shared-form-row">
                <TextField
                  id="city"
                  defaultValue={formObj.city}
                  errorMessage={formErrors.city}
                  textFieldStyle="primary"
                  label="City"
                  automationId="ccpa-form-city-textfield"
                />
                <div className="form-row shared-form-row">
                  <div className="state-select-container">
                    <SuggestionBox
                      id="state"
                      label="State"
                      items={filteredStateItems}
                      selectedItem={stateSelection}
                      errorMessage={formErrors.state}
                      onChange={(item) => {
                        onStateSelect(item);
                      }}
                      automationId="ccpa-form-state-select"
                      aria-label="Please select a state"
                    />
                  </div>

                  <TextField
                    id="zip"
                    defaultValue={formObj.zip}
                    errorMessage={formErrors.zip}
                    textFieldStyle="primary"
                    label="ZIP Code"
                    automationId="request-info-zip-textfield"
                  />
                </div>
              </div>
              <div>
                {/* All states except CA have the sentence about appeal. */}
                <p className="request-type-text caption short">
                  Please indicate which consumer right(s) you wish to invoke by
                  checking the appropriate box(es) below.
                  {!isCA(stateSelection?.value)
                    ? ' You may also appeal the outcome of a prior request as outlined in our privacy policy.'
                    : ''}{' '}
                  Please also indicate the method by which you would like us to
                  deliver this information to you: by email or by mail.
                </p>
                <div className="request">
                  {displayRequestOptions.categories && (
                    <CheckBox
                      id="categories"
                      errorMessage={formErrors.categories}
                      label="Request categories"
                      automationId="request-categories-checkbox"
                      onChange={(e) => {
                        onCategoriesChange(e.target.checked);
                      }}
                    ></CheckBox>
                  )}

                  {displayRequestOptions.knowAccess && (
                    <CheckBox
                      id="knowAccess"
                      errorMessage={formErrors.knowAccess}
                      label="Request to know/access"
                      automationId="request-knowAccess-checkbox"
                      onChange={(e) => {
                        onKnowAccessChange(e.target.checked);
                      }}
                    ></CheckBox>
                  )}

                  <CheckBox
                    id="correction"
                    errorMessage={formErrors.correction}
                    label="Request correction"
                    automationId="request-correction-checkbox"
                    onChange={(e) => {
                      onCorrectionChange(e.target.checked);
                    }}
                  ></CheckBox>

                  {displayRequestOptions.disclosure && (
                    <CheckBox
                      id="disclosure"
                      errorMessage={formErrors.disclosure}
                      label="Request disclosure"
                      automationId="request-disclosure-checkbox"
                      onChange={(e) => {
                        onDisclosureChange(e.target.checked);
                      }}
                    ></CheckBox>
                  )}

                  <CheckBox
                    id="deletion"
                    errorMessage={formErrors.deletion}
                    label="Request deletion"
                    automationId="request-deletion-checkbox"
                    onChange={(e) => {
                      setDeletion(e.target.checked);
                    }}
                  ></CheckBox>

                  {displayRequestOptions.appeal && (
                    <CheckBox
                      id="appeal"
                      errorMessage={formErrors.appeal}
                      label="Request appeal"
                      automationId="request-appeal-checkbox"
                      onChange={(e) => {
                        onAppealChange(e.target.checked);
                      }}
                    ></CheckBox>
                  )}
                </div>
                {formErrors.request ? (
                  <p className="request-error xsmall" key="request-error">
                    {formErrors.request}
                  </p>
                ) : (
                  <p className="request-error xsmall">&nbsp;</p>
                )}
              </div>

              {/* Display the email/address on file option:
                If CA and disclosure or categories is selected - OR - If any other state except CA and know access is selected */}
              {((disclosure || categories) && isCA(stateSelection?.value)) ||
              (knowAccess && !isCA(stateSelection?.value)) ? (
                <div>
                  <h6 className="method-text">
                    How do you want the disclosure sent to you?
                  </h6>
                  <div className="method-buttons">
                    <RadioButtonGroup
                      id="methods"
                      items={methodItems}
                      isLayoutHorizontal={true}
                      onChange={updateMethod}
                    />
                  </div>
                  {formErrors.method ? (
                    <p className="method-error xsmall" key="method-error">
                      {formErrors.method}
                    </p>
                  ) : (
                    <p className="method-error xsmall">&nbsp;</p>
                  )}
                </div>
              ) : null}
              {(disclosure || categories || correction || deletion) &&
              isCA(stateSelection?.value) ? (
                <div>
                  <h6 className="method-text">Are you?</h6>
                  <div className="method-buttons close-to-title">
                    <p className="request-type-text caption short close-to-title">
                      You cannot choose current and former employee.
                    </p>
                    <div className="request">
                      <CheckBox
                        id="customer"
                        errorMessage={formErrors.customer}
                        label="Customer"
                        automationId="request-customer-checkbox"
                        onChange={(e) => {
                          setCustomer(e.target.checked);
                        }}
                      ></CheckBox>
                      <CheckBox
                        id="businessContact"
                        errorMessage={formErrors.businessContact}
                        label="Business Contact"
                        automationId="request-businessContact-checkbox"
                        onChange={(e) => {
                          setBusinessContact(e.target.checked);
                        }}
                      ></CheckBox>
                      <CheckBox
                        id="current"
                        errorMessage={formErrors.current}
                        label="Current Employee"
                        automationId="request-current-checkbox"
                        checked={currentEmployee}
                        onChange={(e) => {
                          onEmployeeTypeChecked(e.target.checked, 'current');
                        }}
                      ></CheckBox>
                      <CheckBox
                        id="former"
                        errorMessage={formErrors.former}
                        label="Former Employee"
                        checked={formerEmployee}
                        automationId="request-former-checkbox"
                        onChange={(e) => {
                          onEmployeeTypeChecked(e.target.checked, 'former');
                        }}
                      ></CheckBox>
                      <CheckBox
                        id="applicant"
                        errorMessage={formErrors.applicant}
                        label="Job Applicant"
                        automationId="request-applicant-checkbox"
                        onChange={(e) => {
                          setApplicant(e.target.checked);
                        }}
                      ></CheckBox>
                    </div>
                  </div>
                  {formErrors.disclosureDetails ? (
                    <p className="details-error xsmall" key="details-error">
                      {formErrors.disclosureDetails}
                    </p>
                  ) : (
                    <p className="details-error xsmall">&nbsp;</p>
                  )}
                </div>
              ) : null}
              {disclosure ||
              deletion ||
              appeal ||
              correction ||
              categories ||
              knowAccess ? (
                ['ca', 'de', 'ne'].includes(stateSelection?.value) ? (
                  // CA acknowledgement has different message options based on other fields when authorized agent is selected.
                  <div className="form-row">
                    <p className="request-type-text caption short">
                      I acknowledge that I am a {stateSelection?.text} resident,
                      or an authorized agent of a {stateSelection?.text}{' '}
                      resident, and am making the request noted above, under the{' '}
                      {privacyActText[stateSelection?.value]?.name}. I
                      acknowledge that I am making this request in good faith.
                    </p>
                    <div className="request left">
                      <CheckBox
                        id="acknowledge"
                        label={`${stateSelection?.value.toUpperCase()} Resident`}
                        automationId="acknowledge-checkbox"
                        checked={acknowledge}
                        onChange={(e) => {
                          onAcknowledgeOrAgentChecked(
                            e.target.checked,
                            'acknowledge'
                          );
                        }}
                      ></CheckBox>
                      <CheckBox
                        id="agent"
                        label="Authorized Agent"
                        automationId="agent-checkbox"
                        checked={agent}
                        onChange={(e) => {
                          onAcknowledgeOrAgentChecked(
                            e.target.checked,
                            'agent'
                          );
                        }}
                      ></CheckBox>
                    </div>
                    {agent && (
                      <p className="request-type-text caption short">
                        {currentEmployee || formerEmployee || applicant
                          ? 'Please contact us at 877-353-6053 or email Personnel@claytonhomes.com to provide proof of authorization.'
                          : 'Please contact us at 877-353-6053 or email CAT@claytonhomes.com to provide proof of authorization.'}
                      </p>
                    )}
                    {formErrors.acknowledge ? (
                      <p
                        className="request-error xsmall"
                        key="acknowledge-error"
                      >
                        {formErrors.acknowledge}
                      </p>
                    ) : (
                      <p className="request-error xsmall">&nbsp;</p>
                    )}
                  </div>
                ) : (
                  // All states except CA have different acknowledge text and button, and no authorized agent option.
                  <div className="form-row">
                    <p className="request-type-text caption short">
                      I acknowledge that I am a {stateSelection?.text} resident,
                      and I am making the request noted above, under the{' '}
                      {privacyActText[stateSelection?.value]?.name}. I
                      acknowledge that I am making this request in good faith.
                    </p>
                    <div className="request">
                      <CheckBox
                        id="acknowledge"
                        label="Acknowledge"
                        automationId="acknowledge-checkbox"
                        onChange={(e) => {
                          setAcknowledge(e.target.checked);
                        }}
                      ></CheckBox>
                    </div>
                    {formErrors.acknowledge ? (
                      <p
                        className="request-error xsmall"
                        key="acknowledge-error"
                      >
                        {formErrors.acknowledge}
                      </p>
                    ) : (
                      <p className="request-error xsmall">&nbsp;</p>
                    )}
                  </div>
                )
              ) : null}
              <Button
                className="ccpa-submit-button"
                aria-label="SUBMIT"
                buttonId="ccpa_submit"
                automationId="ccpa-form-submit"
                fillContainer={true}
                disabled={formSubmitting}
                type="submit"
                onClick={submitClick}
              >
                {!formSubmitting ? (
                  'Submit'
                ) : (
                  <LoadingIndicator
                    color={Colors.primary.white.standard}
                    width={24}
                    height={24}
                  />
                )}
              </Button>
            </Form>
          </div>
        )}

        {formSubmitted && (
          <div>
            <h5>Request received!</h5>
            <p className="thank-you-text intro-paragraph">
              Your information has been submitted for&nbsp;processing.
            </p>
          </div>
        )}
      </div>
    </CCPAFormStyles>
  );
};

export default CCPAForm;
